import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import Header from "../../components/Header";
import Menu from "../../components/Menu";
import Form from "../../../../../components/shared/Form";
import Filters from "./Filters";
import Settings from "./Settings";
import { useColumns } from "./columns";
import { useTeamFilters } from "../../../../TraxxionSchedule/Component/hooks/filters/useTeamFilters";
import { useCompanyPreferences } from "../../hooks/usePreferences";
import { usePreferenceMutation } from "../../api/usePreferenceMutation";
import { useGroups } from "../../../General/OrganisationLevelGroups/context";
import { buildDefaultFilters, flattenFilters } from "../../Timesheet/utils";

const options: string[] = [/**"columns", */ "filters", "settings"];

function Template() {
    const [option, setOption] = useState("filters");
    const columns = useColumns();
    const teamFilters = useTeamFilters();
    const { allocatedGroups } = useGroups();
    const scheduleTemplate = useCompanyPreferences("scheduleTemplate");
    const { mutate, isPending } = usePreferenceMutation("company", "scheduleTemplate");

    // const defaultColumns = useMemo(() =>
    //     buildDefaultArray(columns, scheduleTemplate?.columns ?? []),
    //     [columns, scheduleTemplate]);

    const defaultFilters = useMemo(() =>
        buildDefaultFilters(flattenFilters(teamFilters), scheduleTemplate?.filters ?? []),
        [teamFilters, scheduleTemplate]);

    const formDefaults = useMemo(() => ({
        // columns: defaultColumns,
        filters: defaultFilters,
        defaultFilter: scheduleTemplate?.defaultFilter ?? "",
        defaultPageSize: scheduleTemplate?.defaultPageSize ?? 20,
        shiftCaseDisplay: scheduleTemplate?.shiftCaseDisplay ?? "normal",
        colors: {
            regular: scheduleTemplate?.colors?.regular ?? 0,
        },
        hideSummary: scheduleTemplate?.hideSummary ?? false,
        hideUserProfile: scheduleTemplate?.hideUserProfile ?? false,
        hideDailyInfo: scheduleTemplate?.hideDailyInfo ?? false,
        hidePrint: scheduleTemplate?.hidePrint ?? false,
        shiftDisplay: {
            job: scheduleTemplate?.shiftDisplay?.job ?? true,
            payCode: scheduleTemplate?.shiftDisplay?.payCode ?? true,
            ...Object.fromEntries((allocatedGroups ?? []).map((group: any) => {
                const key = group.level.replaceAll("_", "");
                return [key, scheduleTemplate?.shiftDisplay?.[key] ?? true];
            })),
        },
    }), [defaultFilters, scheduleTemplate, allocatedGroups]);

    const form = useForm({
        defaultValues: formDefaults,
        // `values` is deep-compared by RHF, so a new-but-equal `formDefaults`
        // reference (e.g. from a tab switch re-render) won't reset the form.
        // It only re-syncs when the DB data actually changes, and
        // `keepDirtyValues` keeps the user's in-progress edits.
        values: scheduleTemplate === undefined ? undefined : formDefaults,
        resetOptions: { keepDirtyValues: true },
    });

    const onFinish = (value: typeof formDefaults) => mutate({ ...scheduleTemplate, ...value });

    const panels: { key: string; node: ReactNode }[] = [
        { key: "filters", node: <Filters option="filters" /> },
        { key: "settings", node: <Settings /> },
    ];

    return (
        <div className="tw-p-5 tw-h-full tw-bg-white tw-rounded-lg">
            <Form className="tw-h-full" form={form} onFinish={onFinish}>
                <Header name="template" isLoading={isPending} />
                <Menu
                    options={options}
                    option={option}
                    setOption={setOption}
                />

                <div className="tw-px-7 tw-overflow-y-auto" style={{ height: `calc(100% - 100px)` }}>
                    {panels.map(({ key, node }) => (
                        <div
                            key={key}
                            className="tw-h-full"
                            style={{ display: option === key ? undefined : "none" }}
                        >
                            {node}
                        </div>
                    ))}
                </div>
            </Form>
        </div>
    );
}

export default Template;
